import { ClientDealOfficeFragment, LinkedOfficeFragment, OfficeType, SizeUnit } from 'src/generated/graphql';
import { formatNumber } from 'src/libs/utils';
import { convertAreaUnits, sizeUnits } from 'src/libs/utils/size';

const getOnes = (tens: string) => {
    switch (tens) {
        case '1':
            return 'First';
        case '2':
            return 'Second';
        case '3':
            return 'Third';
        case '4':
            return 'Fourth';
        case '5':
            return 'Fifth';
        case '6':
            return 'Sixth';
        case '7':
            return 'Seventh';
        case '8':
            return 'Eighth';
        case '9':
            return 'Nineth';
        default:
            return '';
    }
};

const getTeens = (teens: string) => {
    switch (teens) {
        case '10':
            return 'Tenth';
        case '11':
            return 'Eleventh';
        case '12':
            return 'Twelfth';
        case '13':
            return 'Thiteenth';
        case '14':
            return 'Fourteenth';
        case '15':
            return 'Fifteenth';
        case '16':
            return 'Sixteenth';
        case '17':
            return 'Seventeenth';
        case '18':
            return 'Eighteenth';
        case '19':
            return 'Nineteenth';
        default:
            return '';
    }
};

const getTens = (tens: string) => {
    switch (tens) {
        case '2':
            return 'Twenty';
        case '3':
            return 'Thirty';
        case '4':
            return 'Forty';
        case '5':
            return 'Fifty';
        case '6':
            return 'Sixty';
        case '7':
            return 'Seventy';
        case '8':
            return 'Eighty';
        case '9':
            return 'Ninety';
        default:
            return '';
    }
};

const getTensOrdienal = (tens: string) => {
    switch (tens) {
        case '2':
            return 'Twentieth';
        case '3':
            return 'Thirtieth';
        case '4':
            return 'Fortieth';
        case '5':
            return 'Fiftieth';
        case '6':
            return 'Sixtieth';
        case '7':
            return 'Seventieth';
        case '8':
            return 'Eightieth';
        case '9':
            return 'Ninetieh';
        default:
            return '';
    }
};

const getHundreds = (tens: string) => {
    switch (tens) {
        case '1':
            return 'Hundred';
        case '2':
            return 'Two Hundred';
        case '3':
            return 'Three Hundred';
        case '4':
            return 'Four Hundred';
        case '5':
            return 'Five Hundred';
        case '6':
            return 'Six Hundred';
        case '7':
            return 'Seven Hundred';
        case '8':
            return 'Eight Hundred';
        case '9':
            return 'Nine Hundred';
        default:
            return '';
    }
};

export const getTotalSize = (
    desks?: number,
    size?: number,
    sizeUnit: SizeUnit = SizeUnit.SquareFeet,
    linkedByOffice?: LinkedOfficeFragment[],
) => {
    let linkedOfficeSize = 0;
    let linkedOfficeDesks = 0;
    if (linkedByOffice) {
        linkedByOffice.forEach(((office) => {
            linkedOfficeSize += office?.size && office.sizeUnit !== sizeUnit
                ? convertAreaUnits(office.size, office.sizeUnit) || 0
                : office.size || 0;
            linkedOfficeDesks += office.desks || 0;
        }));
    }
    return ({
        desks: (desks || 0) + linkedOfficeDesks,
        size: (size || 0) + linkedOfficeSize,
    });
};

export const sizeToText = (
    desks?: number,
    size?: number,
    sizeUnit: SizeUnit = SizeUnit.SquareFeet,
    useRSF?: boolean
): string => {
    const sizeRSF = useRSF ? 'RSF' : sizeUnits[sizeUnit];
    if ((desks && desks > 0) && (size && size > 0)) {
        return `${desks} desks / ${formatNumber(size, { style: 'decimal' })} ${sizeRSF}`;
    }
    if (desks && desks > 0) {
        return `Up to ${desks} desks`;
    }

    if (size && size > 0) {
        return `Up to ${formatNumber(size, { style: 'decimal' })} ${sizeRSF}`;
    }
    return '';
};

export const toWords = (number: string) => {
    switch (number.length) {
        case 1:
            if (number === '0') {
                return 'Ground';
            }

            return getOnes(number);

        case 2:
            if (number[0] === '-') {
                return 'Lower Ground';
            }
            if (number[0] === '1') {
                return getTeens(number);
            }

            return number[1] === '0' ? getTensOrdienal(number[0]) : `${getTens(number[0])}-${getOnes(number[1])}`;

        case 3:
            if (number[1] === '1') {
                return `${getHundreds(number[0])}and${getTeens(number.slice(1))}`;
            }

            return `${getHundreds(number[0])}and${number[2]}` === '0'
                ? getTensOrdienal(number[1])
                : `${getTens(number[1])}-${getOnes(number[2])}`;

        default: {
            return '';
        }
    }
};

export const hasImportantAmenity = (amenity:string, amenities: {id: string, name: string}[]) => {
    const exists = amenities.find((a) => a.name.toLowerCase().includes(amenity));
    return exists;
};

const americanDealId = 'cltspoydo519371p2xt04ymf91';

export const getTotalCost = (office: ClientDealOfficeFragment, dealId: string) => {
    const initialCost = office.totalCost || 0;
    const hasTotal = office.linkedToOffice.length > 0 ? 'total' : '';
    const hasPerMonth = office.officeType === OfficeType.Purchase ? '' : 'p/month';
    const totalCost = office.linkedToOffice.length > 0
        ? office.linkedToOffice.reduce((acc, linkedOffice) => acc + (linkedOffice.totalCost || 0), initialCost)
        : initialCost;

    return `${formatNumber(
        (dealId === americanDealId ? totalCost * 1.27 : totalCost) || 0,
        undefined,
        (dealId === americanDealId ? 'USD' : office.property.region.currency.id)
    )} ${hasTotal} ${hasPerMonth}`;
};
